import { supabase } from '@/integrations/supabase/client';

const DEFAULT_BACKEND_URL = (typeof window !== 'undefined' && (window as any).VITE_ALPACA_BACKEND_URL) || import.meta?.env?.VITE_ALPACA_BACKEND_URL || 'https://stewie.api.clewis.me';
const DEBUG = (import.meta as any)?.env?.VITE_DEBUG_ALPACA_BACKEND === 'true';

export type TradingMode = 'paper' | 'live';

export interface OrderRequest {
  symbol: string;
  qty: number;
  type: 'market' | 'limit' | 'stop' | 'stop_limit';
  time_in_force: 'day' | 'gtc' | 'opg' | 'cls' | 'ioc' | 'fok';
  limit_price?: number;
  stop_price?: number;
  side?: 'buy' | 'sell';
}

export interface AlpacaCredentials {
  api_key_id: string;
  api_secret_key: string;
  trading_mode: TradingMode;
}

async function getJwt(): Promise<string | null> {
  const { data, error } = await supabase.auth.getSession();
  if (error) {
    if (DEBUG) console.error('[AlpacaBackend] Supabase session error:', error.message);
    return null;
  }
  const token = data?.session?.access_token || null;
  if (DEBUG) console.log('[AlpacaBackend] Retrieved JWT:', token ? 'present' : 'missing');
  return token;
}

async function request<T>(path: string, options: RequestInit = {}, baseUrl: string = DEFAULT_BACKEND_URL): Promise<T> {
  const token = await getJwt();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const url = `${baseUrl.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
  if (DEBUG) console.log('[AlpacaBackend] Request:', url, options.method || 'GET');

  const res = await fetch(url, { ...options, headers });
  if (DEBUG) console.log('[AlpacaBackend] Response status:', res.status);

  if (res.status === 401 || res.status === 403) {
    // Attempt token refresh via Supabase; the client auto-refreshes, but we can hint
    await supabase.auth.getSession();
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Alpaca backend error ${res.status}: ${text || res.statusText}`);
  }

  // Some endpoints may return 204 No Content
  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}

class AlpacaBackendService {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = (baseUrl || DEFAULT_BACKEND_URL).replace(/\/$/, '');
  }

  // Configuration
  setBaseUrl(url: string) {
    this.baseUrl = url.replace(/\/$/, '');
  }

  // Account
  async getAccount(): Promise<any> {
    return request<any>('/account', {}, this.baseUrl);
  }

  // Credentials (one-time per user)
  async setAlpacaCredentials(creds: AlpacaCredentials): Promise<{ success: boolean } | any> {
    return request('/broker/credentials', {
      method: 'POST',
      body: JSON.stringify(creds),
    }, this.baseUrl);
  }

  async getAlpacaCredentials(): Promise<AlpacaCredentials | any> {
    return request('/broker/credentials', {}, this.baseUrl);
  }

  // Trading operations
  async buyStock(order: OrderRequest): Promise<any> {
    const payload = { ...order, side: 'buy' as const };
    return request('/buy', {
      method: 'POST',
      body: JSON.stringify(payload),
    }, this.baseUrl);
  }

  async sellStock(order: OrderRequest): Promise<any> {
    const payload = { ...order, side: 'sell' as const };
    return request('/sell', {
      method: 'POST',
      body: JSON.stringify(payload),
    }, this.baseUrl);
  }

  // Orders
  async getOrders(accountId?: string): Promise<any[]> {
    const q = accountId ? `?account_id=${encodeURIComponent(accountId)}` : '';
    return request(`/broker/orders${q}`, {}, this.baseUrl);
  }

  async getOrder(orderId: string): Promise<any> {
    return request(`/orders/${encodeURIComponent(orderId)}`, {}, this.baseUrl);
  }

  async cancelOrder(accountId: string, orderId: string): Promise<any> {
    return request(`/broker/accounts/${encodeURIComponent(accountId)}/orders/${encodeURIComponent(orderId)}`, {
      method: 'DELETE',
    }, this.baseUrl);
  }

  // Positions
  async getPositions(accountId?: string): Promise<any[]> {
    const q = accountId ? `?account_id=${encodeURIComponent(accountId)}` : '';
    return request(`/broker/positions${q}`, {}, this.baseUrl);
  }

  async getPosition(symbol: string): Promise<any> {
    return request(`/positions/${encodeURIComponent(symbol)}`, {}, this.baseUrl);
  }

  async closePosition(accountId: string, symbol: string): Promise<any> {
    return request(`/broker/accounts/${encodeURIComponent(accountId)}/positions/${encodeURIComponent(symbol)}`, {
      method: 'DELETE',
    }, this.baseUrl);
  }

  // Trade data
  async getTrades(params?: Record<string, string>): Promise<any[]> {
    const q = params ? `?${new URLSearchParams(params).toString()}` : '';
    return request(`/trades${q}`, {}, this.baseUrl);
  }

  async getAlpacaTrades(params?: Record<string, string>): Promise<any[]> {
    const q = params ? `?${new URLSearchParams(params).toString()}` : '';
    return request(`/trades/alpaca${q}`, {}, this.baseUrl);
  }

  async getSupabaseTrades(params?: Record<string, string>): Promise<any[]> {
    const q = params ? `?${new URLSearchParams(params).toString()}` : '';
    return request(`/trades/supabase${q}`, {}, this.baseUrl);
  }
}

export const alpacaBackendService = new AlpacaBackendService();
